import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { IUser } from 'interfaces/user.interface'
import type { PersistedAuthState } from './auth.types'
import {
  loadPersistedAuth,
  persistAuth,
  clearPersistedAuth,
  isTokenExpired,
} from '../lib/token.utils'

/**
 * Auth store with localStorage persistence
 * - Access token and expiry stored in localStorage
 * - User data fetched from separate endpoint (optional)
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const accessToken = ref<string | null>(null)
  const expiresAt = ref<number | null>(null)
  const user = ref<IUser | null>(null)

  // Computed
  const isAuthenticated = computed(() => !!accessToken.value && !!expiresAt.value)

  const isExpired = computed(() => {
    if (!expiresAt.value) return true
    return isTokenExpired(expiresAt.value)
  })

  /**
   * Initialize auth state from localStorage
   * Call this on app startup
   */
  function initializeFromStorage(): boolean {
    const stored = loadPersistedAuth()

    if (!stored) {
      return false
    }

    // Check if stored token is already expired
    if (isTokenExpired(stored.expiresAt)) {
      expiresAt.value = stored.expiresAt
      accessToken.value = null
      return false
    }

    // Valid tokens found
    accessToken.value = stored.accessToken
    expiresAt.value = stored.expiresAt

    return true
  }

  /**
   * Set auth state after successful login
   */
  function setTokens(state: PersistedAuthState): void {
    accessToken.value = state.accessToken
    expiresAt.value = state.expiresAt

    // Persist to localStorage
    persistAuth(state)
  }

  /**
   * Set user data (from separate endpoint)
   */
  function setUser(userData: IUser): void {
    user.value = userData
  }

  /**
   * Clear all auth state on logout
   */
  function clearAuth(): void {
    accessToken.value = null
    expiresAt.value = null
    user.value = null

    // Clear from localStorage
    clearPersistedAuth()
  }

  return {
    // State
    accessToken,
    expiresAt,
    user,

    // Computed
    isAuthenticated,
    isExpired,

    // Actions
    initializeFromStorage,
    setTokens,
    setUser,
    clearAuth,
  }
})
